import React from "react";
import structuredDataConfig from "@data/config/structured-data.json";

/**
 * Shared StructuredData component for JSON-LD Schema markup
 * Supports multiple schema types for SEO
 */

// Base organization data from JSON
const BASE_ORGANIZATION = structuredDataConfig.baseOrganization;

// Organization Schema — @type and @id come from baseOrganization (structured-data.json)
const getOrganizationSchema = () => ({
  "@context": "https://schema.org",
  ...BASE_ORGANIZATION,
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    ...structuredDataConfig.offerCatalog,
  },
});

// SoftwareApplication Schema
const getSoftwareApplicationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  ...structuredDataConfig.softwareApplication,
  author: {
    "@type": "Organization",
    name: BASE_ORGANIZATION.name,
    url: BASE_ORGANIZATION.url,
  },
  publisher: {
    "@type": "Organization",
    name: BASE_ORGANIZATION.name,
    url: BASE_ORGANIZATION.url,
  },
  dateModified: new Date().toISOString().split("T")[0],
});

// FAQPage Schema
interface FAQItem {
  question: string;
  answer: string;
}

const getFAQPageSchema = (faqs: FAQItem[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
});

// BreadcrumbList Schema
interface BreadcrumbItem {
  position: number;
  name: string;
  item: string;
}

const getBreadcrumbListSchema = (items: BreadcrumbItem[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item) => ({
    "@type": "ListItem",
    position: item.position,
    name: item.name,
    item: item.item,
  })),
});

// WebSite Schema
const getWebSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": "https://dbi-analytics.de/#website",
  name: structuredDataConfig.webSite.name,
  url: BASE_ORGANIZATION.url,
  description: BASE_ORGANIZATION.description,
  inLanguage: "de-DE",
  /* No potentialAction/SearchAction: the site has no /search route, and a
     declared action pointing at a 404 is worse than declaring none. Re-add it
     together with a real search page, not before. */
  publisher: { "@id": "https://dbi-analytics.de/#organization" },
});

// CaseStudy Schema (for Success Stories)
interface CaseStudyData {
  metadata: {
    title: string;
    description: string;
    canonical: string;
    keywords?: string[];
  };
  hero: {
    title: string;
    subtitle: string;
  };
  results?: {
    title?: string;
    subtitle?: string;
    metrics: Array<{
      number?: string;
      value?: string;
      label: string;
      description?: string;
    }>;
    projectDetails?: {
      plannedDuration?: string;
      actualDuration?: string;
    };
    description?: string;
  };
  testimonial?: {
    quote: string;
    author: string;
    role: string;
  };
  timeline?: {
    milestones: Array<{
      date: string;
      title: string;
      description: string;
    }>;
  };
}

const getCaseStudySchema = (
  data: CaseStudyData,
  organizationName: string = BASE_ORGANIZATION.name,
  organizationUrl: string = BASE_ORGANIZATION.url
) => {
  // Remove HTML tags for clean text in structured data
  const cleanDescription = data.metadata.description.replace(/<[^>]*>/g, "");
  const cleanQuote = data.testimonial?.quote.replace(/<[^>]*>/g, "") || "";

  // Build full description, optionally embedding the testimonial quote
  const fullDescription =
    data.testimonial && cleanQuote
      ? `${cleanDescription} – „${cleanQuote}" (${data.testimonial.author}, ${data.testimonial.role})`
      : cleanDescription;

  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: data.hero.title,
    description: fullDescription,
    url: `${organizationUrl}${data.metadata.canonical}`,
    inLanguage: "de-DE",
    datePublished: new Date().toISOString().split("T")[0],
    dateModified: new Date().toISOString().split("T")[0],
    author: {
      "@type": "Organization",
      name: organizationName,
      url: organizationUrl,
    },
    publisher: {
      "@type": "Organization",
      name: organizationName,
      url: organizationUrl,
      logo: {
        "@type": "ImageObject",
        url: `${organizationUrl}/logo-word.svg`,
      },
    },
    about: {
      "@type": "Service",
      name: data.hero.title,
      provider: {
        "@type": "Organization",
        name: organizationName,
        url: organizationUrl,
      },
      serviceType: "Business Intelligence Consulting",
      areaServed: {
        "@type": "Country",
        name: "Deutschland",
      },
    },
    keywords: data.metadata.keywords?.join(", ") ?? "",
  };

  // Add timeline milestones as article parts
  if (data.timeline?.milestones) {
    schema.hasPart = data.timeline.milestones.map((milestone) => ({
      "@type": "CreativeWork",
      name: `${milestone.date}: ${milestone.title}`,
      description: milestone.description?.replace(/<[^>]*>/g, "") ?? "",
    }));
  }

  return schema;
};

// Service Schema (ProfessionalService)
interface ServiceSchemaData {
  name: string;
  description: string;
  url: string;
  provider?: string;
  areaServed?: string;
  serviceType?: string;
  keywords?: string[];
  image?: string;
  offers?: Array<{
    name: string;
    description: string;
  }>;
  hasOfferCatalog?: Array<{
    name: string;
    description: string;
  }>;
  aggregateRating?: {
    ratingValue: string;
    ratingCount: string;
  };
}

const getServiceSchema = (
  data: ServiceSchemaData,
  organizationName: string = BASE_ORGANIZATION.name,
  organizationUrl: string = BASE_ORGANIZATION.url
) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: data.name,
  description: data.description,
  url: data.url,
  image: data.image,
  provider: {
    "@type": "Organization",
    name: organizationName,
    url: organizationUrl,
    logo: `${organizationUrl}/logo-word.svg`,
    sameAs: BASE_ORGANIZATION.sameAs,
  },
  areaServed: {
    "@type": "Country",
    name: data.areaServed ?? "Deutschland",
  },
  serviceType: data.serviceType ?? "Business Intelligence Consulting",
  knowsAbout: data.keywords ?? [],
  ...(data.hasOfferCatalog && {
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: data.name,
      itemListElement: data.hasOfferCatalog.map((offer) => ({
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: offer.name,
          description: offer.description,
        },
      })),
    },
  }),
  ...(data.aggregateRating && {
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: data.aggregateRating.ratingValue,
      ratingCount: data.aggregateRating.ratingCount,
      bestRating: "5",
      worstRating: "1",
    },
  }),
});

// VideoObject Schema
interface VideoSchemaData {
  name: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string;
  duration: string;
  contentUrl: string;
  embedUrl: string;
}

const getVideoSchema = (data: VideoSchemaData) => ({
  "@context": "https://schema.org",
  "@type": "VideoObject",
  name: data.name,
  description: data.description,
  thumbnailUrl: data.thumbnailUrl,
  uploadDate: data.uploadDate,
  duration: data.duration,
  contentUrl: data.contentUrl,
  embedUrl: data.embedUrl,
  publisher: {
    "@type": "Organization",
    name: BASE_ORGANIZATION.name,
    url: BASE_ORGANIZATION.url,
    logo: {
      "@type": "ImageObject",
      url: `${BASE_ORGANIZATION.url}/logo-word.svg`,
    },
  },
});

// HowTo Schema (for process steps)
interface HowToStep {
  name: string;
  text: string;
  url?: string;
}

interface HowToSchemaData {
  name: string;
  description: string;
  url?: string;
  steps: HowToStep[];
  totalTime?: string;
}

const getHowToSchema = (
  data: HowToSchemaData,
  organizationUrl: string = BASE_ORGANIZATION.url
) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: data.name,
  description: data.description,
  ...(data.totalTime && { totalTime: data.totalTime }),
  step: data.steps.map((step, index) => ({
    "@type": "HowToStep",
    position: index + 1,
    name: step.name,
    text: step.text,
    ...(step.url && { url: `${organizationUrl}${step.url}` }),
  })),
});

// Main component interface
interface StructuredDataProps {
  type:
    | "Organization"
    | "SoftwareApplication"
    | "FAQPage"
    | "BreadcrumbList"
    | "WebSite"
    | "CaseStudy"
    | "Service"
    | "VideoObject"
    | "HowTo";
  // Optional props for specific schema types
  faqs?: FAQItem[];
  breadcrumbs?: BreadcrumbItem[];
  caseStudyData?: CaseStudyData;
  serviceData?: ServiceSchemaData;
  videoData?: VideoSchemaData;
  howToData?: HowToSchemaData;
  organizationName?: string;
  organizationUrl?: string;
}

/**
 * Shared StructuredData component
 * Renders JSON-LD schema markup for SEO
 */
export default function StructuredData({
  type,
  faqs,
  breadcrumbs,
  caseStudyData,
  serviceData,
  videoData,
  howToData,
  organizationName = BASE_ORGANIZATION.name,
  organizationUrl = BASE_ORGANIZATION.url,
}: StructuredDataProps) {
  const getStructuredData = () => {
    switch (type) {
      case "Organization":
        return getOrganizationSchema();
      case "SoftwareApplication":
        return getSoftwareApplicationSchema();
      case "FAQPage":
        if (!faqs) {
          console.warn("FAQPage schema requires 'faqs' prop");
          return {};
        }
        return getFAQPageSchema(faqs);
      case "BreadcrumbList":
        if (!breadcrumbs) {
          console.warn("BreadcrumbList schema requires 'breadcrumbs' prop");
          return {};
        }
        return getBreadcrumbListSchema(breadcrumbs);
      case "WebSite":
        return getWebSiteSchema();
      case "CaseStudy":
        if (!caseStudyData) {
          console.warn("CaseStudy schema requires 'caseStudyData' prop");
          return {};
        }
        return getCaseStudySchema(caseStudyData, organizationName, organizationUrl);
      case "Service":
        if (!serviceData) {
          console.warn("Service schema requires 'serviceData' prop");
          return {};
        }
        return getServiceSchema(serviceData, organizationName, organizationUrl);
      case "HowTo":
        if (!howToData) {
          console.warn("HowTo schema requires 'howToData' prop");
          return {};
        }
        return getHowToSchema(howToData, organizationUrl);
      case "VideoObject":
        if (!videoData) {
          console.warn("VideoObject schema requires 'videoData' prop");
          return {};
        }
        return getVideoSchema(videoData);
      default:
        return {};
    }
  };

  const structuredData = getStructuredData();

  // Don't render if no data
  if (!structuredData || Object.keys(structuredData).length === 0) {
    return null;
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(structuredData, null, 2),
      }}
    />
  );
}

// Export helper function for multiple schemas (e.g., Success Stories need multiple)
export function renderMultipleSchemas(schemas: Array<{ type: StructuredDataProps["type"]; props?: Omit<StructuredDataProps, "type"> }>) {
  return (
    <>
      {schemas.map((schema, index) => (
        <StructuredData key={index} type={schema.type} {...schema.props} />
      ))}
    </>
  );
}

// Export types for use in other components
export type {
  CaseStudyData,
  FAQItem,
  BreadcrumbItem,
  ServiceSchemaData,
  VideoSchemaData,
  HowToSchemaData,
};

